import { Injectable } from '@angular/core';
import { PortalApiService } from './portal-api.service';

export interface PreviewRole {
	id: number;
	name: string;
}

export interface PreviewScope {
	project_id: number;
	role_id: number | null;
}

// The parent (WeQuote) owns the staff session, so it is the only thing that can mint a preview.
// These are the two directions of that conversation.
const MSG_READY = 'wq-portal-preview-ready';
const MSG_SWITCH = 'wq-portal-preview-switch';
const MSG_CODE = 'wq-portal-preview-code';
const MSG_ERROR = 'wq-portal-preview-error';

// One-way: the cover-image editor pushing the framing it is editing, so the project hero shows an
// unsaved crop. Nothing is sent back — the editor saves through its own API, not through here.
const MSG_COVER = 'wq-portal-cover';

const HANDSHAKE_TIMEOUT_MS = 8000;

/**
 * A staff preview of this portal, framed inside WeQuote.
 *
 * The token lives here and nowhere else — not a cookie, not sessionStorage. That is what lets two
 * previews of different projects be open at the same time: each iframe has its own instance of this
 * service, so they cannot see or overwrite each other. It also means nothing is left behind in the
 * browser when the modal closes.
 *
 * Switching role or project does NOT re-key the session here. It asks the parent for a brand new
 * one, because only the parent has the staff session that is allowed to decide scope. A preview
 * token therefore can never widen the scope it was issued for.
 */
@Injectable({ providedIn: 'root' })
export class PortalPreviewService {

	// True once this looks like a preview, even if the handshake later fails — the SPA needs to know
	// not to fall back to the login page. `marked` distinguishes the first load (which carries the
	// marker) from a reload deeper in, and decides whether a failed handshake is worth reporting.
	active = false;
	marked = false;
	ready = false;
	failed = false;
	failureMessage = '';

	token: string | null = null;

	// Whether the launch actually named a project. When it didn't, the backend started the preview on
	// a sensible one, and the portal opens on the projects list so staff pick for themselves.
	projectChosen = false;

	roles: PreviewRole[] = [];
	current: PreviewScope | null = null;
	switching = false;

	/**
	 * A cover-image preview: WeQuote's cover editor framing this portal to show the crop being
	 * edited. `coverMode` strips the preview chrome so the page looks exactly as the customer will
	 * see it; `cover` is the unsaved framing the project hero uses in place of the saved one.
	 */
	coverMode = false;
	cover: any = null;

	/**
	 * Tooltip for a control a preview isn't allowed to use.
	 *
	 * Those controls are disabled rather than left clickable, so the explanation belongs on the
	 * control itself — a message elsewhere on the page would make the reader hunt for the connection.
	 */
	readonly blockedReason = 'Not available in preview';

	private parentOrigin: string | null = null;
	private pending: ((msg: any) => void) | null = null;

	constructor(private api: PortalApiService) {}

	/**
	 * Whether to attempt a preview handshake. Called before anything else boots, from AppComponent.
	 *
	 * Being framed is the signal, not the URL. The marker only survives the first load — Angular
	 * rewrites /portal-preview to / on redirect, and staff then navigate deeper — so a reload inside
	 * the preview would otherwise look like an ordinary visit and bounce to the login page.
	 *
	 * Only WeQuote can frame the portal (frame-ancestors), so a framed portal is a preview. Where
	 * that assumption doesn't hold — dev, which serves no CSP — an unmarked handshake that gets no
	 * reply falls back to the normal session instead of erroring, which is what `marked` is for.
	 */
	detect(): boolean {
		const params = new URLSearchParams(window.location.search);

		this.marked = /^\/portal-preview(\/|$)/.test(window.location.pathname) || !!params.get('origin');
		this.coverMode = params.get('mode') === 'cover';
		this.active = window.parent !== window;
		return this.active;
	}

	/**
	 * Settle the preview before the app starts, from an APP_INITIALIZER.
	 *
	 * It has to be an initialiser rather than AppComponent.ngOnInit: the router's first navigation
	 * runs the auth guard, which asks for the session. Without the token in place by then, the guard
	 * sees "not signed in" and sends the preview to the login page.
	 *
	 * Resolves either way — a preview that can't start is reported by `failed`, not by rejecting.
	 */
	bootstrap(): Promise<boolean> {
		if (!this.detect()) return Promise.resolve(false);

		return this.handshake().then(ok => {
			if (ok) return true;

			// The URL said this was a preview, so a failure is real and worth showing. Otherwise the
			// page just happened to be framed — carry on as an ordinary visit.
			if (this.marked) return false;

			this.standDown();
			return false;
		});
	}

	/**
	 * Ask the parent frame for a session and redeem it.
	 *
	 * Refuses outright when there is no parent: a preview URL opened directly is meant to be worth
	 * nothing, and this is the client half of that (the server half is that no token is ever issued
	 * without a code, and the code only reaches a framed page).
	 */
	private handshake(): Promise<boolean> {
		this.active = true;

		if (window.parent === window) {
			return Promise.resolve(this.fail('This preview can only be opened from WeQuote.'));
		}

		// Where the first message is aimed. On a reload the marker is gone, so this can be unknown —
		// in which case the origin is pinned from whoever replies. Safe either way: the outbound
		// message carries nothing secret, and a reply can't be used without a valid code.
		this.parentOrigin = this.expectedParentOrigin();

		window.addEventListener('message', this.onMessage);

		return this.request({ type: MSG_READY }).then(msg => {
			this.roles = msg.roles || [];
			this.current = msg.current || null;
			this.projectChosen = !!msg.project_chosen;
			return this.redeem(msg.code);
		}, message => this.fail(message));
	}

	/**
	 * Move the preview to a different project and/or role.
	 *
	 * Resolves once the new token is in place. The caller (the banner) then re-routes, because which
	 * sections are visible may have changed with the role.
	 */
	switchTo(scope: PreviewScope): Promise<boolean> {
		if (this.switching) return Promise.resolve(false);
		this.switching = true;

		const previous = this.current;

		return this.request({
			type: MSG_SWITCH,
			project_id: scope.project_id,
			role_id: scope.role_id
		}).then(msg => {
			if (msg.roles) this.roles = msg.roles;

			return this.redeem(msg.code).then(ok => {
				this.switching = false;

				if (!ok) {
					this.current = previous;
					return false;
				}

				this.current = msg.current || scope;
				this.projectChosen = true;
				return true;
			});
		}, message => {
			this.switching = false;
			this.current = previous;
			this.failureMessage = message;
			return false;
		});
	}

	// Swap a one-time code for the bearer token every later request carries.
	private redeem(code: string): Promise<boolean> {
		if (!code) return Promise.resolve(this.fail('This preview session has expired. Reopen it from WeQuote.'));

		return new Promise<boolean>(resolve => {
			this.api.preview.start({ code }, (res: any) => {
				this.token = res?.token || null;
				// Pushed into the API service rather than pulled from here: the API service is the
				// one thing every other service depends on, so it must not depend back on this one.
				this.api.previewToken = this.token;

				if (!this.token) {
					resolve(this.fail('This preview session has expired. Reopen it from WeQuote.'));
					return;
				}
				this.ready = true;
				this.failed = false;
				this.failureMessage = '';
				resolve(true);
			}, (err: any) => {
				resolve(this.fail(err?.message || 'This preview session has expired. Reopen it from WeQuote.'));
			});
		});
	}

	// One request/response exchange with the parent frame.
	private request(payload: any): Promise<any> {
		return new Promise<any>((resolve, reject) => {
			let settled = false;

			const timer = setTimeout(() => {
				if (settled) return;
				settled = true;
				this.pending = null;
				reject('WeQuote did not respond. Please reopen the preview.');
			}, HANDSHAKE_TIMEOUT_MS);

			this.pending = (msg: any) => {
				if (settled) return;
				settled = true;
				clearTimeout(timer);
				this.pending = null;

				if (msg.type === MSG_ERROR) {
					reject(msg.message || 'WeQuote could not open this preview.');
					return;
				}

				resolve(msg);
			};

			window.parent.postMessage(payload, this.parentOrigin ?? '*');
		});
	}

	private onMessage = (event: MessageEvent) => {
		// Only ever the frame that opened this preview.
		if (event.source !== window.parent) return;

		const msg = event.data;
		if (!msg) return;

		// Only ever after the handshake, so the origin is already pinned — an unpinned origin means
		// this arrived before a session existed, and there is nothing it could legitimately be.
		if (msg.type === MSG_COVER) {
			if (this.parentOrigin && event.origin === this.parentOrigin) this.cover = msg.cover || null;
			return;
		}

		if (msg.type !== MSG_CODE && msg.type !== MSG_ERROR) return;

		// Pin to whoever answered first, then hold every later message to it — so a switch can't be
		// driven by a different frame than the one that opened the preview.
		if (this.parentOrigin === null) this.parentOrigin = event.origin;
		if (event.origin !== this.parentOrigin) return;

		this.pending?.(msg);
	};

	/**
	 * Where the opening message is aimed, from ?origin= on the preview URL, or null when absent.
	 *
	 * Parsed rather than trusted as a string so it can only ever be an origin, never a path or
	 * credentials. It is not the security boundary — frame-ancestors decides who may frame the
	 * preview at all, and the code is single-use — it just avoids broadcasting the opening message
	 * when the address already says who to talk to.
	 */
	private expectedParentOrigin(): string | null {
		const raw = new URLSearchParams(window.location.search).get('origin') || '';
		if (!raw) return null;

		try {
			const url = new URL(raw);
			if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
			return url.origin;
		} catch {
			return null;
		}
	}

	/**
	 * Give up on the preview and let the portal behave normally.
	 *
	 * For a framed page that turned out not to be a preview — nothing was minted, so there is nothing
	 * to clean up beyond forgetting that we tried.
	 */
	standDown(): void {
		this.active = false;
		this.failed = false;
		this.failureMessage = '';
		window.removeEventListener('message', this.onMessage);
	}

	private fail(message: string): boolean {
		this.failed = true;
		this.ready = false;
		this.token = null;
		this.api.previewToken = null;
		this.failureMessage = message;
		return false;
	}
}
